import { BLOCK_KEYS, CHARACTER_KEYS, OBJECT_KEYS } from "../../constants";
import { blockImgsAndGifs, characterImgs, objectImgs } from "../../assets";

export const GLOSSARY_TAB_KEYS = {
  BLOCKS: "blocks",
  OBJECTS: "objects",
  CHARACTERS: "characters",
};

export const TAB_SECTION_KEYS = {
  [GLOSSARY_TAB_KEYS.BLOCKS]: {
    states: "states",
    connectors: "connectors",
  },
};

const PROCESS_DATA_FUNCS = {
  // filters data and modifies thingsToConsider info data depending on lesson number
  [GLOSSARY_TAB_KEYS.BLOCKS]: (data, { lessonNum }) =>
    data
      .filter(({ showLessonNum }) => showLessonNum <= lessonNum)
      .map(
        ({
          info: { thingsToConsider, thingsToConsiderAfterLesson, ...otherInfo },
          ...otherItemData
        }) => ({
          ...otherItemData,
          info: {
            ...otherInfo,
            thingsToConsider: [
              ...thingsToConsider,
              ...(thingsToConsiderAfterLesson
                ? Array.from(Array(lessonNum + 1).keys()).reduce(
                    (arr, lessonNumKey) => {
                      const additionalThingsToConsider =
                        thingsToConsiderAfterLesson?.[lessonNumKey];
                      if (additionalThingsToConsider) {
                        arr.push(...additionalThingsToConsider);
                      }
                      return arr;
                    },
                    []
                  )
                : []),
            ],
          },
        })
      )
      .sort(sortItemsAlphabetically),
  // filters data depending on current lesson number
  [GLOSSARY_TAB_KEYS.OBJECTS]: (data, { lessonNum }) =>
    data
      .filter(({ appearsInLesson }) => appearsInLesson[lessonNum])
      .sort(sortItemsAlphabetically),
  // filters data depending on current lesson number
  [GLOSSARY_TAB_KEYS.CHARACTERS]: (data, { lessonNum }) =>
    data.filter(
      ({ showInfo, hideInfo }) =>
        showInfo.lessonNum <= lessonNum &&
        (!hideInfo || lessonNum < hideInfo.lessonNum)
    ),
};

export const GLOSSARY_DATA_BY_TAB = {
  [GLOSSARY_TAB_KEYS.BLOCKS]: {
    // NOTE: if anything affecting the title width is changed (i.e. text, font, letter spacing),
    // tabIndicatorWidth must be manually updated to be 2px less than the half width of the DOM text width
    title: "Blocks",
    tabIndicatorWidth: "46.875px",
    processData: PROCESS_DATA_FUNCS[GLOSSARY_TAB_KEYS.BLOCKS],
    [TAB_SECTION_KEYS.blocks.states]: {
      title: "State Blocks",
      data: [
        {
          key: BLOCK_KEYS.you,
          label: "You",
          info: {
            definition: "An object that is YOU is controllable by the player.",
            thingsToConsider: [
              "Any object can be YOU - not just Sol!",
              "Multiple objects can be YOU at the same time.",
              "If no objects in the problem are YOU, the player must undo or reset.",
            ],
            gif: blockImgsAndGifs.you.gif,
          },
          showLessonNum: 1,
          img: blockImgsAndGifs.you.img,
        },
        {
          key: BLOCK_KEYS.win,
          label: "Win",
          info: {
            definition:
              "To complete a problem, a player must be on the same space as an object that is WIN.",
            thingsToConsider: [
              "WIN objects will have sparkles around them.",
              "WIN objects can be destroyed due to other states - a player must undo or reset if there are no WIN objects left in the puzzle.",
              "Most puzzles already have an object that is WIN from the start. However, some puzzles may require the player to write a rule for the WIN object.",
            ],
            gif: blockImgsAndGifs.win.gif,
          },
          showLessonNum: 1,
          img: blockImgsAndGifs.win.img,
        },
        {
          key: BLOCK_KEYS.stop,
          label: "Stop",
          info: {
            definition:
              "If an object is STOP, other objects cannot move into that object's space.",
            thingsToConsider: [
              "Other states may help the player get past objects that are STOP.",
            ],
            gif: blockImgsAndGifs.stop.gif,
          },
          showLessonNum: 1,
          img: blockImgsAndGifs.stop.img,
        },
        {
          key: BLOCK_KEYS.push,
          label: "Push",
          info: {
            definition:
              "If an object is PUSH, the player can move that object by moving into it.",
            thingsToConsider: [
              "If an object is pushed against a STOP object or the edge of a puzzle, it cannot be pulled back in the opposite direction unless the player undoes or resets.",
            ],
            gif: blockImgsAndGifs.push.gif,
          },
          img: blockImgsAndGifs.push.img,
          showLessonNum: 1,
        },
        {
          key: BLOCK_KEYS.defeat,
          label: "Defeat",
          info: {
            definition:
              "If the player moves into an object that is DEFEAT, the player's character will be destroyed. The player must then undo or reset.",
            thingsToConsider: [
              "DEFEAT objects do not destroy any objects other than the YOU object.",
            ],
            gif: blockImgsAndGifs.defeat.gif,
          },
          showLessonNum: 1,
          img: blockImgsAndGifs.defeat.img,
        },
        {
          key: BLOCK_KEYS.weak,
          label: "Weak",
          info: {
            definition:
              "If an object is WEAK, it will be destroyed upon touching any other object.",
            thingsToConsider: [
              "An object that is WEAK can also be destroyed when pushed against the edge of the puzzle or against any object that is STOP.",
            ],
            gif: blockImgsAndGifs.weak.gif,
          },
          showLessonNum: 2,
          img: blockImgsAndGifs.weak.img,
        },
        {
          key: BLOCK_KEYS.boom,
          label: "Boom",
          info: {
            definition:
              "If an object is BOOM, it will be destroyed immediately upon rule formation. Any objects on the same space as a BOOM object will also be destroyed.",
            thingsToConsider: [
              "Since making an object BOOM has an immediate effect, be sure to consider the timing of when to make these rules. For example, a player may need to push an object into a specific space before making that object BOOM.",
            ],
            gif: blockImgsAndGifs.boom.gif,
          },
          showLessonNum: 2,
          img: blockImgsAndGifs.boom.img,
        },
        {
          key: BLOCK_KEYS.sink,
          label: "Sink",
          info: {
            definition:
              "If an object is SINK, it will destroy any other object that moves onto its space, along with itself.",
            thingsToConsider: [
              "SINK objects can be obstacles, but they can also be used to the player's advantage in some cases. For example, an object that is PUSH and SINK can be pushed onto certain obstacles to destroy them.",
            ],
            gif: blockImgsAndGifs.sink.gif,
          },
          showLessonNum: 2,
          img: blockImgsAndGifs.sink.img,
        },
        {
          key: BLOCK_KEYS.hot,
          label: "Hot",
          info: {
            definition:
              "An object that is HOT will destroy any object that is MELT if they are on the same space.",
            thingsToConsider: [
              "HOT and MELT are paired states -- HOT objects will only destroy MELT objects.",
              "When a HOT object destroys a MELT object, the HOT object does not get destroyed.",
            ],
            thingsToConsiderAfterLesson: {
              4: [
                "This differs from OPEN and SHUT, in which both the OPEN and SHUT objects mutually destroy each other.",
              ],
            },
            gif: blockImgsAndGifs.hot.gif,
          },
          showLessonNum: 3,
          img: blockImgsAndGifs.hot.img,
        },
        {
          key: BLOCK_KEYS.melt,
          label: "Melt",
          info: {
            definition:
              "An object that is MELT will be destroyed if it's on the same space as an object that is HOT.",
            thingsToConsider: [
              "HOT and MELT are paired states -- MELT objects are only destroyed by HOT objects.",
              "While HOT objects destroy MELT objects, the reverse is not true -- MELT objects do not destroy HOT objects.",
            ],
            thingsToConsiderAfterLesson: {
              4: [
                "This differs from OPEN and SHUT, in which both the OPEN and SHUT objects mutually destroy each other.",
              ],
            },
            gif: blockImgsAndGifs.melt.gif,
          },
          showLessonNum: 3,
          img: blockImgsAndGifs.melt.img,
        },
        {
          key: BLOCK_KEYS.open,
          label: "Open",
          info: {
            definition:
              "If an object that is OPEN touches an object that is SHUT, both objects will be destroyed.",
            thingsToConsider: [
              "OPEN and SHUT are paired states -- they can only destroy each other.",
              "The interaction between OPEN and SHUT differs from that between HOT and MELT -- whereas MELT objects cannot destroy HOT objects, OPEN and SHUT objects mutually destroy each other.",
              "OPEN objects can be obstacles in a problem if paired with states like STOP or DEFEAT.",
            ],
            gif: blockImgsAndGifs.open.gif,
          },
          showLessonNum: 4,
          img: blockImgsAndGifs.open.img,
        },
        {
          key: BLOCK_KEYS.shut,
          label: "Shut",
          info: {
            definition:
              "If an object that is SHUT touches an object that is OPEN, both objects will be destroyed.",
            thingsToConsider: [
              "OPEN and SHUT are paired states -- they can only destroy each other.",
              "The interaction between OPEN and SHUT differs from that between HOT and MELT -- whereas MELT objects cannot destroy HOT objects, OPEN and SHUT objects mutually destroy each other.",
              "SHUT objects are not necessarily obstacles -- an object that is SHUT does not have the STOP state by default.",
            ],
            gif: blockImgsAndGifs.shut.gif,
          },
          showLessonNum: 4,
          img: blockImgsAndGifs.shut.img,
        },
        {
          key: BLOCK_KEYS.safe,
          label: "Safe",
          info: {
            definition: "If an object is SAFE, it cannot be destroyed.",
            thingsToConsider: [
              "• SAFE objects can still destroy other objects.",
              "There is no way to destroy a SAFE object other than breaking the SAFE rule or transforming the object into something else.",
            ],
            gif: blockImgsAndGifs.safe.gif,
          },
          showLessonNum: 7,
          img: blockImgsAndGifs.safe.img,
        },
        {
          key: BLOCK_KEYS.goright,
          label: "Go Right",
          info: {
            definition:
              "When an object has the GO RIGHT state, that object will move one space RIGHT every time the player moves one space.",
            thingsToConsider: [
              "An object that is WEAK can also be destroyed when pushed against the edge of the puzzle or against any object that is STOP.",
            ],
            gif: blockImgsAndGifs.goright.gif,
          },
          showLessonNum: 7,
          img: blockImgsAndGifs.goright.img,
        },
        {
          key: BLOCK_KEYS.goleft,
          label: "Go Left",
          info: {
            definition:
              "When an object has the GO LEFT state, that object will move one space LEFT every time the player moves one space.",
            thingsToConsider: [
              "An object that is WEAK can also be destroyed when pushed against the edge of the puzzle or against any object that is STOP.",
            ],
            gif: blockImgsAndGifs.goleft.gif,
          },
          showLessonNum: 7,
          img: blockImgsAndGifs.goleft.img,
        },
        {
          key: BLOCK_KEYS.goup,
          label: "Go Up",
          info: {
            definition:
              "When an object has the GO UP state, that object will move one space UP every time the player moves one space.",
            thingsToConsider: [
              "An object that is WEAK can also be destroyed when pushed against the edge of the puzzle or against any object that is STOP.",
            ],
            gif: blockImgsAndGifs.goup.gif,
          },
          showLessonNum: 7,
          img: blockImgsAndGifs.goup.img,
        },
        {
          key: BLOCK_KEYS.godown,
          label: "Go Down",
          info: {
            definition:
              "When an object has the GO DOWN state, that object will move one space DOWN every time the player moves one space.",
            thingsToConsider: [
              "An object that is WEAK can also be destroyed when pushed against the edge of the puzzle or against any object that is STOP.",
            ],
            gif: blockImgsAndGifs.godown.gif,
          },
          showLessonNum: 7,
          img: blockImgsAndGifs.godown.img,
        },
      ],
    },
    [TAB_SECTION_KEYS.blocks.connectors]: {
      title: "Connectors",
      data: [
        {
          key: BLOCK_KEYS.is,
          label: "Is",
          info: {
            definition:
              "The IS block is a connector block that allows for the formation of rules. It is typically used to assign a state to an object, but can also be used to transform an object into a different object.",
            thingsToConsider: [
              "Rules must be written in a straight, contiguous line. The blocks must be written in order, either from left to right or top to bottom.",
              "When an object tranforms into a new object, it behaves according to the rules for the new object only.",
            ],
            gif: blockImgsAndGifs.is.gif,
          },
          showLessonNum: 1,
          img: blockImgsAndGifs.is.img,
        },
        {
          key: BLOCK_KEYS.and,
          label: "And",
          info: {
            definition:
              "AND is a connector block that can be used to give an object multiple states within a single rule. It can also be used to give multiple objects a specified state within a single rule.",
            thingsToConsider: [
              "The AND block also allows the player to assign multiple states to multiple objects in a single rule.",
            ],
            gif: blockImgsAndGifs.and.gif,
          },
          showLessonNum: 4,
          img: blockImgsAndGifs.and.img,
        },
        {
          key: BLOCK_KEYS.not,
          label: "Not",
          info: {
            definition:
              "NOT is a connector block that can be used to cancel out rules. For example, WATER IS NOT SINK will cancel out the rule WATER IS SINK.",
            thingsToConsider: [
              "A rule that is canceled out by a NOT rule will have red X's on top of it.",
              "NOT can also be used to prevent object transformations.",
            ],
            gif: blockImgsAndGifs.not.gif,
          },
          showLessonNum: 8,
          img: blockImgsAndGifs.not.img,
        },
      ],
    },
  },
  [GLOSSARY_TAB_KEYS.OBJECTS]: {
    // NOTE: if anything affecting the title width is changed (i.e. text, font, letter spacing),
    // tabIndicatorWidth must be manually updated to be 2px less than the half width of the DOM text width
    title: "Objects",
    tabIndicatorWidth: "53.6px",
    processData: PROCESS_DATA_FUNCS[GLOSSARY_TAB_KEYS.OBJECTS],
    data: [
      {
        key: OBJECT_KEYS.heart,
        label: "Heart",
        appearsInLesson: {
          1: true,
          2: true,
          3: true,
          4: true,
          5: true,
          6: true,
          7: true,
          8: true,
          9: true,
          10: true,
        },
        img: objectImgs.heart.img,
      },
      {
        key: OBJECT_KEYS.sandcastle,
        label: "Sandcastle",
        appearsInLesson: {
          1: true,
          5: true,
          6: true,
          9: true,
        },
        img: objectImgs.sandcastle.img,
      },
      {
        key: OBJECT_KEYS.bubble,
        label: "Bubble",
        appearsInLesson: {
          1: true,
          4: true,
          5: true,
          6: true,
          9: true,
        },
        img: objectImgs.bubble.img,
      },
      {
        key: OBJECT_KEYS.crabCowboy,
        label: "Crab",
        appearsInLesson: {
          1: true,
          5: true,
          6: true,
          10: true,
        },
        img: objectImgs.crabCowboy.img,
      },
      {
        key: OBJECT_KEYS.rocksGrey,
        label: "Rock",
        appearsInLesson: {
          1: true,
        },
        img: objectImgs.rocksGrey.img,
      },
      {
        key: OBJECT_KEYS.fossil,
        label: "Fossil",
        appearsInLesson: {
          1: true,
          2: true,
          3: true,
          4: true,
          9: true,
          10: true,
        },
        img: objectImgs.fossil.img,
      },
      {
        key: OBJECT_KEYS.mushroom,
        label: "Mushroom",
        appearsInLesson: {
          1: true,
        },
        img: objectImgs.mushroom.img,
      },
      {
        key: OBJECT_KEYS.gold,
        label: "Gold",
        appearsInLesson: {
          2: true,
          9: true,
        },
        img: objectImgs.gold.img,
      },
      {
        key: OBJECT_KEYS.fungus,
        label: "Mushroom",
        appearsInLesson: {
          2: true,
        },
        img: objectImgs.fungus.img,
      },
      {
        key: OBJECT_KEYS.spike,
        label: "Crystal",
        appearsInLesson: {
          2: true,
          3: true,
          4: true,
          6: true,
          9: true,
        },
        img: objectImgs.spike.img,
      },
      {
        key: OBJECT_KEYS.water,
        label: "Water",
        appearsInLesson: {
          2: true,
          4: true,
          5: true,
          6: true,
          8: true,
          9: true,
        },
        img: objectImgs.water.img,
      },
      {
        key: OBJECT_KEYS.rocksBlue,
        label: "Rock",
        appearsInLesson: {
          2: true,
          9: true,
        },
        img: objectImgs.rocksBlue.img,
      },
      {
        key: OBJECT_KEYS.ice,
        label: "Ice",
        appearsInLesson: {
          3: true,
          9: true,
        },
        img: objectImgs.ice.img,
      },
      {
        key: OBJECT_KEYS.snow,
        label: "Snow",
        appearsInLesson: {
          3: true,
          9: true,
        },
        img: objectImgs.snow.img,
      },
      {
        key: OBJECT_KEYS.snowbunny,
        label: "Snowbunny",
        appearsInLesson: {
          3: true,
        },
        img: objectImgs.snowbunny.img,
      },
      {
        key: OBJECT_KEYS.snowbear,
        label: "Snowbear",
        appearsInLesson: {
          3: true,
          9: true,
        },
        img: objectImgs.snowbear.img,
      },
      {
        key: OBJECT_KEYS.lavaPit,
        label: "Lava",
        appearsInLesson: {
          3: true,
          9: true,
        },
        img: objectImgs.lavaPit.img,
      },
      {
        key: OBJECT_KEYS.fire,
        label: "Fire",
        appearsInLesson: {
          3: true,
          9: true,
          10: true,
        },
        img: objectImgs.fire.img,
      },
      {
        key: OBJECT_KEYS.puddle,
        label: "Water",
        appearsInLesson: {
          3: true,
          6: true,
          7: true,
          8: true,
          9: true,
          10: true,
        },
        img: objectImgs.puddle.img,
      },
      {
        key: OBJECT_KEYS.snowball,
        label: "Snowball",
        appearsInLesson: {
          3: true,
          9: true,
        },
        img: objectImgs.snowball.img,
      },
      {
        key: OBJECT_KEYS.rocksSnow,
        label: "Rock",
        appearsInLesson: {
          3: true,
        },
        img: objectImgs.rocksSnow.img,
      },
      {
        key: OBJECT_KEYS.grass,
        label: "Grass",
        appearsInLesson: {
          4: true,
          7: true,
          8: true,
          9: true,
        },
        img: objectImgs.grass.img,
      },
      {
        key: OBJECT_KEYS.frog,
        label: "Frog",
        appearsInLesson: {
          4: true,
          7: true,
          8: true,
          9: true,
        },
        img: objectImgs.frog.img,
      },
      {
        key: OBJECT_KEYS.fly,
        label: "Fly",
        appearsInLesson: {
          4: true,
          7: true,
          8: true,
        },
        img: objectImgs.fly.img,
      },
      {
        key: OBJECT_KEYS.rocksAlgae,
        label: "Rock",
        appearsInLesson: {
          4: true,
          6: true,
          8: true,
          9: true,
        },
        img: objectImgs.rocksAlgae.img,
      },
      {
        key: OBJECT_KEYS.lavaUnderwater,
        label: "Lava",
        appearsInLesson: {
          4: true,
          6: true,
          7: true,
          8: true,
          10: true,
        },
        img: objectImgs.lavaUnderwater.img,
      },
      {
        key: OBJECT_KEYS.moat,
        label: "Water",
        appearsInLesson: {
          6: true,
          7: true,
          9: true,
        },
        img: objectImgs.moat.img,
      },
      {
        key: OBJECT_KEYS.starfish,
        label: "Starfish",
        appearsInLesson: {
          5: true,
          6: true,
          9: true,
        },
        img: objectImgs.starfish.img,
      },
      {
        key: OBJECT_KEYS.rocksTidepool,
        label: "Rock",
        appearsInLesson: {
          5: true,
          6: true,
        },
        img: objectImgs.rocksTidepool.img,
      },
      {
        key: OBJECT_KEYS.seaweed,
        label: "Seaweed",
        appearsInLesson: {
          5: true,
          6: true,
        },
        img: objectImgs.seaweed.img,
      },
      {
        key: OBJECT_KEYS.ball,
        label: "Ball",
        appearsInLesson: {
          5: true,
          6: true,
          9: true,
        },
        img: objectImgs.ball.img,
      },
      {
        key: OBJECT_KEYS.pearl,
        label: "Pearl",
        appearsInLesson: {
          6: true,
        },
        img: objectImgs.pearl.img,
      },
      {
        key: OBJECT_KEYS.urchin,
        label: "Urchin",
        appearsInLesson: {
          5: true,
          6: true,
        },
        img: objectImgs.urchin.img,
      },
      {
        key: OBJECT_KEYS.crab,
        label: "Crab",
        appearsInLesson: {
          6: true,
        },
        img: objectImgs.crab.img,
      },
      {
        key: OBJECT_KEYS.shrub,
        label: "Shrub",
        appearsInLesson: {
          6: true,
          7: true,
          8: true,
          9: true,
          10: true,
        },
        img: objectImgs.shrub.img,
      },
      {
        key: OBJECT_KEYS.box,
        label: "Box",
        appearsInLesson: {
          7: true,
          9: true,
        },
        img: objectImgs.box.img,
      },
      {
        key: OBJECT_KEYS.wallPlatformPlantsOrange,
        label: "Wall",
        appearsInLesson: {
          6: true,
        },
        img: objectImgs.wallPlatformPlantsOrange.img,
      },
      {
        key: OBJECT_KEYS.wallPlatformPlantsGrey,
        label: "Wall",
        appearsInLesson: {
          7: true,
          8: true,
          9: true,
        },
        img: objectImgs.wallPlatformPlantsGrey.img,
      },
      {
        key: OBJECT_KEYS.wallPlatformGrey,
        label: "Wall",
        appearsInLesson: {
          7: true,
          9: true,
        },
        img: objectImgs.wallPlatformGrey.img,
      },
      {
        key: OBJECT_KEYS.chicken,
        label: "Chicken",
        appearsInLesson: {
          7: true,
          8: true,
          9: true,
        },
        img: objectImgs.chicken.img,
      },
      {
        key: OBJECT_KEYS.sword,
        label: "Sword",
        appearsInLesson: {
          7: true,
          9: true,
          10: true,
        },
        img: objectImgs.sword.img,
      },
      {
        key: OBJECT_KEYS.watermelon,
        label: "Watermelon",
        appearsInLesson: {
          8: true,
        },
        img: objectImgs.watermelon.img,
      },
      {
        key: OBJECT_KEYS.carrots,
        label: "Carrots",
        appearsInLesson: {
          8: true,
        },
        img: objectImgs.carrots.img,
      },
      {
        key: OBJECT_KEYS.apple,
        label: "Apple",
        appearsInLesson: {
          8: true,
          9: true,
        },
        img: objectImgs.apple.img,
      },
      {
        key: OBJECT_KEYS.soilSprout,
        label: "Soil",
        appearsInLesson: {
          8: true,
          9: true,
        },
        img: objectImgs.soilSprout.img,
      },
      {
        key: OBJECT_KEYS.wallPlatformOrange,
        label: "Wall",
        appearsInLesson: {
          9: true,
        },
        img: objectImgs.wallPlatformOrange.img,
      },
      {
        key: OBJECT_KEYS.dresserFloral,
        label: "Dresser",
        appearsInLesson: {
          10: true,
        },
        img: objectImgs.dresserFloral.img,
      },
      {
        key: OBJECT_KEYS.bookshelf,
        label: "Bookshelf",
        appearsInLesson: {
          10: true,
        },
        img: objectImgs.bookshelf.img,
      },
      {
        key: OBJECT_KEYS.book,
        label: "Book",
        appearsInLesson: {
          10: true,
        },
        img: objectImgs.book.img,
      },
      {
        key: OBJECT_KEYS.rugTeal,
        label: "Rug",
        appearsInLesson: {
          10: true,
        },
        img: objectImgs.rugTeal.img,
      },
      {
        key: OBJECT_KEYS.tableLine,
        label: "Table",
        appearsInLesson: {
          10: true,
        },
        img: objectImgs.tableLine.img,
      },
    ],
  },
  [GLOSSARY_TAB_KEYS.CHARACTERS]: {
    // NOTE: if anything affecting the title width is changed (i.e. text, font, letter spacing),
    // tabIndicatorWidth must be manually updated to be 2px less than the half width of the DOM text width
    title: "Characters",
    tabIndicatorWidth: "77.2px",
    processData: PROCESS_DATA_FUNCS[GLOSSARY_TAB_KEYS.CHARACTERS],
    // data should be sorted by order of character appearance
    data: [
      {
        key: CHARACTER_KEYS.sol,
        label: "Sol",
        showInfo: { lessonNum: 1 },
        img: characterImgs.sol.img,
      },
      {
        key: CHARACTER_KEYS.luna,
        label: "Luna",
        showInfo: { lessonNum: 1 },
        img: characterImgs.luna.img,
      },
      // two versions of masked menti with different labels
      {
        key: CHARACTER_KEYS.mentiMasked,
        label: "Mysterious Knight",
        showInfo: { lessonNum: 2 },
        hideInfo: { lessonNum: 7 },
        img: characterImgs.mentiMasked.img,
      },
      // replaces first version of masked menti
      {
        key: CHARACTER_KEYS.mentiMasked,
        label: "Menti",
        showInfo: { lessonNum: 7 },
        hideInfo: { lessonNum: 8 },
        img: characterImgs.mentiMasked.img,
      },
      {
        key: CHARACTER_KEYS.zorro,
        label: "Zorro",
        showInfo: { lessonNum: 3 },
        img: characterImgs.zorro.img,
      },
      {
        key: CHARACTER_KEYS.piney,
        label: "Piney",
        showInfo: { lessonNum: 4 },
        img: characterImgs.piney.img,
      },
      {
        key: CHARACTER_KEYS.morel,
        label: "Morel",
        showInfo: { lessonNum: 4 },
        img: characterImgs.morel.img,
      },
      {
        key: CHARACTER_KEYS.enoki,
        label: "Enoki",
        showInfo: { lessonNum: 4 },
        img: characterImgs.enoki.img,
      },
      {
        key: CHARACTER_KEYS.olive,
        label: "Olive",
        showInfo: { lessonNum: 5 },
        hideInfo: { lessonNum: 6 },
        img: characterImgs.olive.img,
      },
      // replaces regular olive
      {
        key: CHARACTER_KEYS.oliveBowl,
        label: "Olive",
        showInfo: { lessonNum: 6 },
        img: characterImgs.oliveBowl.img,
      },
      {
        key: OBJECT_KEYS.crabCowboy,
        label: "Helpful Crab",
        showInfo: { lessonNum: 6 },
        img: objectImgs.crabCowboy.img,
      },
      // replaces masked menti
      {
        key: CHARACTER_KEYS.mentiUnmasked,
        label: "Menti",
        showInfo: { lessonNum: 8 },
        img: characterImgs.mentiUnmasked.img,
      },
      {
        key: CHARACTER_KEYS.lopi,
        label: "Lopi",
        showInfo: { lessonNum: 8 },
        img: characterImgs.lopi.img,
      },
      {
        key: CHARACTER_KEYS.lino,
        label: "Lino",
        showInfo: { lessonNum: 10 },
        img: characterImgs.lino.img,
      },
    ],
  },
};

const sortItemsAlphabetically = (a, b) =>
  a.label > b.label ? 1 : a.label === b.label ? 0 : -1;
